import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTrash,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaMapMarkerAlt,
  FaLinkedin,
  FaSync,
  FaBriefcase,
  FaGraduationCap,
  FaTags,
  FaExternalLinkAlt,
  FaUserPlus
} from "react-icons/fa";
import Swal from "sweetalert2";

const LinkedInResultsPanel = ({
  projectId,
  apiUrl,
  activeProfiles = [],
  onDiscardSuccess,
  loading = false,
  onClose,
  onRefresh,
}) => {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [confirmDiscardUrl, setConfirmDiscardUrl] = useState(null);
  const [discardingUrls, setDiscardingUrls] = useState(new Set());
  const [isDiscardingAll, setIsDiscardingAll] = useState(false);
  const [addingUrls, setAddingUrls] = useState(new Set());
  const [addedUrls, setAddedUrls] = useState(new Set());

  // Listen for extension add-profile response
  useEffect(() => {
    const handleExtensionResult = (event) => {
      if (event.source !== window || !event.data) return;

      if (event.data.type === "EPH_ADD_PROFILE_RESULT") {
        const { response, url } = event.data;
        const targetUrl = url;

        if (targetUrl) {
          setAddingUrls((prev) => {
            const next = new Set(prev);
            next.delete(targetUrl);
            return next;
          });
        } else {
          setAddingUrls(new Set());
        }

        if (response?.success) {
          if (targetUrl) {
            setAddedUrls((prev) => new Set(prev).add(targetUrl));
          }
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: `${response.scrapedName || "Profile"} added to project!`,
            showConfirmButton: false,
            timer: 3500,
            timerProgressBar: true,
          });
          if (onRefresh) {
            onRefresh();
          }
        } else {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: response?.error || "Failed to add profile to project",
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
          });
        }
      }
    };

    window.addEventListener("message", handleExtensionResult);
    return () => {
      window.removeEventListener("message", handleExtensionResult);
    };
  }, [onRefresh]);

  const handleAddProfileViaExtension = (candidate) => {
    if (!candidate) return;
    const targetUrl = getTargetProfileUrl(candidate);
    if (!targetUrl) {
      Swal.fire({
        icon: "warning",
        title: "No Profile URL",
        text: "Could not find a valid LinkedIn profile URL for this candidate.",
      });
      return;
    }

    const cleanName = getCleanName(candidate.name);

    setAddingUrls((prev) => new Set(prev).add(targetUrl));

    const eventPayload = {
      url: targetUrl,
      projectId: projectId,
      name: cleanName,
      candidateName: cleanName,
    };

    window.dispatchEvent(
      new CustomEvent("eph-trigger-add-profile-to-project", {
        detail: eventPayload,
      })
    );
    window.postMessage(
      {
        type: "EPH_TRIGGER_ADD_PROFILE_TO_PROJECT",
        payload: eventPayload,
      },
      "*"
    );

    // 45s fallback timeout
    setTimeout(() => {
      setAddingUrls((prev) => {
        if (prev.has(targetUrl)) {
          const next = new Set(prev);
          next.delete(targetUrl);
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "info",
            title: "Extension timed out or not detected. Please ensure the EPH Chrome Extension is active.",
            showConfirmButton: false,
            timer: 5000,
            timerProgressBar: true,
          });
          return next;
        }
        return prev;
      });
    }, 45000);
  };

  // Helper to clean candidate name
  const getCleanName = (rawName) => {
    if (!rawName) return "LinkedIn Profile";
    let clean = rawName;
    for (const phrase of [
      "was last active",
      "is active",
      "1st degree connection",
      "2nd degree connection",
      "3rd degree connection",
      "Add ",
      " to selection",
    ]) {
      if (clean.includes(phrase)) {
        clean = clean.split(phrase)[0];
      }
    }
    return clean.trim() || rawName;
  };

  // Helper to clean candidate location and strip degree connection artifacts
  const getCleanLocation = (rawLoc) => {
    if (!rawLoc) return null;
    const locStr = String(rawLoc).trim();
    const lower = locStr.toLowerCase();
    if (
      lower.includes("degree") ||
      lower.includes("connection") ||
      lower.includes("viewed") ||
      lower.includes("active") ||
      lower.includes("selection") ||
      lower === "3rd" || lower === "2nd" || lower === "1st" ||
      lower === "· 3rd" || lower === "· 2nd" || lower === "· 1st" ||
      lower === "• 3rd" || lower === "• 2nd" || lower === "• 1st"
    ) {
      return null;
    }
    let clean = locStr.replace(/^[·•\s\-\|]+/, "").trim();
    return clean || null;
  };

  // Helper to resolve canonical target profile URL
  const getTargetProfileUrl = (candidate) => {
    if (!candidate) return "";
    const cleanName = getCleanName(candidate.name);
    return candidate.public_url && !candidate.public_url.includes('/sales/lead/')
      ? candidate.public_url
      : (candidate.profile_url && !candidate.profile_url.includes('/sales/lead/')
        ? candidate.profile_url
        : (cleanName && cleanName !== "LinkedIn Member"
          ? `https://www.linkedin.com/in/${encodeURIComponent(cleanName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))}`
          : candidate.profile_url));
  };

  // Helper to parse strings separated by pipes or newlines into arrays
  const parseList = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    return String(val)
      .split(/\s*\|\s*|\n+/)
      .map((s) => s.trim())
      .filter((s) => s && s.length > 0);
  };

  // Helper to parse comma/pipe separated skills
  const parseSkills = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    return String(val)
      .split(/[,|;]+/)
      .map((s) => s.trim())
      .filter((s) => s && s.length > 0);
  };

  const isSelected = (candidate) => {
    if (!selectedProfile || !candidate) return false;
    const sUrl = selectedProfile.profile_url || selectedProfile.public_url;
    const cUrl = candidate.profile_url || candidate.public_url;
    return sUrl && cUrl && sUrl === cUrl;
  };

  const handleDiscardConfirm = async (profileUrl) => {
    setDiscardingUrls((prev) => {
      const next = new Set(prev);
      next.add(profileUrl);
      return next;
    });

    const tokenObj = localStorage.getItem("eph-token");
    const token = tokenObj ? JSON.parse(tokenObj).value : null;

    try {
      const response = await fetch(`${apiUrl}projects/${projectId}/linkedin-profiles/discard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({ profile_url: profileUrl }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        onDiscardSuccess(profileUrl);
        setConfirmDiscardUrl(null);
        if (selectedProfile && (selectedProfile.profile_url === profileUrl || selectedProfile.public_url === profileUrl)) {
          setSelectedProfile(null);
        }
      } else {
        throw new Error(data.message || "Something went wrong while discarding the profile");
      }
    } catch (error) {
      console.error("Error discarding candidate:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to discard the candidate. Please try again.",
      });
    } finally {
      setDiscardingUrls((prev) => {
        const next = new Set(prev);
        next.delete(profileUrl);
        return next;
      });
    }
  };

  const handleDiscardAll = async () => {
    if (activeProfiles.length === 0) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to discard all recommendations for this project.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, discard all",
      cancelButtonText: "Cancel"
    });

    if (!result.isConfirmed) return;

    setIsDiscardingAll(true);
    setSelectedProfile(null);

    const tokenObj = localStorage.getItem("eph-token");
    const token = tokenObj ? JSON.parse(tokenObj).value : null;

    try {
      const discardPromises = activeProfiles.map(async (profile) => {
        try {
          const response = await fetch(`${apiUrl}projects/${projectId}/linkedin-profiles/discard`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": token,
            },
            body: JSON.stringify({ profile_url: profile.profile_url }),
          });
          const data = await response.json();
          if (response.ok && data.status === "success") {
            return profile.profile_url;
          }
        } catch (err) {
          console.error("Failed to discard profile", profile.profile_url, err);
        }
        return null;
      });

      const discardedUrls = await Promise.all(discardPromises);

      discardedUrls.forEach((url) => {
        if (url) {
          onDiscardSuccess(url);
        }
      });

      Swal.fire({
        icon: "success",
        title: "Discarded",
        text: "All profiles have been discarded successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error("Error discarding all profiles:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to discard all profiles. Please try again.",
      });
    } finally {
      setIsDiscardingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="linkedin-results-panel">
        <div className="linkedin-loading-container" style={{ width: "100%", padding: "40px" }}>
          <FaSpinner className="fa-spin" size={32} style={{ color: "#0a66c2" }} />
          <p>Loading recommendations...</p>
        </div>
      </div>
    );
  }

  // Selected profile parsed details
  const selectedName = selectedProfile ? getCleanName(selectedProfile.name) : "";
  const selectedLocation = selectedProfile ? getCleanLocation(selectedProfile.location) : null;
  const selectedFitScore = selectedProfile ? (selectedProfile.fit_score || selectedProfile.score || 8) : 8;
  const selectedScoreClass = selectedFitScore >= 8 ? "high" : selectedFitScore >= 6 ? "medium" : "low";
  const selectedTargetUrl = selectedProfile ? getTargetProfileUrl(selectedProfile) : "";
  const parsedExperience = selectedProfile ? parseList(selectedProfile.experience) : [];
  const parsedSkills = selectedProfile ? parseSkills(selectedProfile.skills) : [];
  const parsedEducation = selectedProfile ? parseList(selectedProfile.education) : [];

  return (
    <div className={`linkedin-results-panel ${selectedProfile ? "has-detail" : ""}`}>
      {/* Columna Izquierda: Master List */}
      <div className="linkedin-master-pane">
        <div className="linkedin-results-header" style={{ gap: '10px' }}>
          <div>
            <h2>LinkedIn Recommendations</h2>
            <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>
              {activeProfiles.length} active
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {activeProfiles.length > 0 && (
              <button
                onClick={handleDiscardAll}
                disabled={isDiscardingAll}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d32f2f',
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  color: '#d32f2f',
                  cursor: isDiscardingAll ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  opacity: isDiscardingAll ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDiscardingAll) e.currentTarget.style.backgroundColor = '#ffebee';
                }}
                onMouseLeave={(e) => {
                  if (!isDiscardingAll) e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                {isDiscardingAll ? (
                  <>
                    <FaSpinner className="fa-spin" /> Discarding...
                  </>
                ) : (
                  "Discard All"
                )}
              </button>
            )}
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                title="Refresh recommendations"
                style={{
                  background: "none",
                  border: "none",
                  cursor: loading ? "not-allowed" : "pointer",
                  padding: "6px",
                  display: "flex",
                  alignItems: "center",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                <FaSync className={loading ? "fa-spin" : ""} />
              </button>
            )}
            {onClose && (
              <button className="advanced-filters-close-btn" onClick={onClose} title="Close">
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div className="linkedin-results-content">
          {activeProfiles.length === 0 ? (
            <div className="linkedin-empty-state">
              <FaLinkedin size={48} style={{ color: "#ccc" }} />
              <h3>No recommendations found</h3>
              <p style={{ margin: "0" }}>
                Active recommendations for this project will appear here.
              </p>
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <AnimatePresence initial={false}>
                {activeProfiles.map((candidate, index) => {
                  const isConfirming = confirmDiscardUrl === candidate.profile_url;
                  const isDiscarding = discardingUrls.has(candidate.profile_url);

                  const cleanName = getCleanName(candidate.name);
                  const cleanLocation = getCleanLocation(candidate.location);
                  const reasoningText = candidate.ai_reasoning || candidate.reasoning;
                  const fitScore = candidate.fit_score || candidate.score || 8;
                  const scoreClass = fitScore >= 8 ? "high" : fitScore >= 6 ? "medium" : "low";

                  const isHeadlineGeneric = candidate.headline && (
                    candidate.headline.includes("degree connection") ||
                    candidate.headline.includes("Add ") ||
                    candidate.headline.toLowerCase().includes("view profile")
                  );

                  const targetProfileUrl = getTargetProfileUrl(candidate);
                  const selectedThis = isSelected(candidate);

                  return (
                    <motion.div
                      key={candidate.profile_url || candidate.public_url || index}
                      layout
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                      className={`linkedin-profile-card ${selectedThis ? "is-selected" : ""}`}
                      onClick={() => setSelectedProfile(candidate)}
                    >
                      <div className="linkedin-profile-header">
                        <div
                          className="linkedin-profile-name"
                          title="Click to view details in side window"
                          style={{ cursor: "pointer" }}
                        >
                          <FaLinkedin size={18} style={{ color: "#0a66c2", flexShrink: 0 }} />
                          <span>{cleanName}</span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span className={`fit-score-badge ${scoreClass}`} title="AI Fit Alignment Score">
                            Fit Score: {fitScore}/10
                          </span>
                        </div>
                      </div>

                      {candidate.headline && !isHeadlineGeneric && (
                        <p className="linkedin-profile-headline">{candidate.headline}</p>
                      )}

                      {cleanLocation && (
                        <div className="linkedin-profile-location">
                          <FaMapMarkerAlt size={12} style={{ color: "#888" }} />
                          <span>{cleanLocation}</span>
                        </div>
                      )}

                      {reasoningText && (
                        <div className="linkedin-profile-reasoning">
                          <div className="linkedin-profile-reasoning-title">AI Match Evaluation</div>
                          {reasoningText}
                        </div>
                      )}

                      {candidate.search_query && (
                        <div className="linkedin-profile-query" title="Search query match">
                          Match: "{candidate.search_query}"
                        </div>
                      )}

                      <div className="linkedin-profile-actions" onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontSize: "11px", color: "#0a66c2", fontWeight: "600" }}>
                            {selectedThis ? "Viewing Details →" : "View Details →"}
                          </span>
                        </div>

                        <div className="linkedin-profile-actions-right">
                          <button
                            type="button"
                            className={`linkedin-add-btn ${
                              addingUrls.has(targetProfileUrl)
                                ? "loading"
                                : addedUrls.has(targetProfileUrl)
                                ? "added"
                                : ""
                            }`}
                            disabled={addingUrls.has(targetProfileUrl) || addedUrls.has(targetProfileUrl)}
                            onClick={() => handleAddProfileViaExtension(candidate)}
                            title={
                              addedUrls.has(targetProfileUrl)
                                ? "Profile already added to project"
                                : addingUrls.has(targetProfileUrl)
                                ? "Scraping and adding to project..."
                                : "Open profile and add to project via EPH Extension"
                            }
                          >
                            {addingUrls.has(targetProfileUrl) ? (
                              <>
                                <FaSpinner className="fa-spin" size={11} />
                                <span>Adding...</span>
                              </>
                            ) : addedUrls.has(targetProfileUrl) ? (
                              <>
                                <FaCheck size={11} />
                                <span>Added</span>
                              </>
                            ) : (
                              <>
                                <FaUserPlus size={11} />
                                <span>Add to Project</span>
                              </>
                            )}
                          </button>

                          <AnimatePresence mode="wait">
                          {isDiscarding ? (
                            <motion.div
                              key="loader"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#666" }}
                            >
                              <FaSpinner className="fa-spin" size={14} style={{ color: "#0a66c2" }} />
                              Discarding...
                            </motion.div>
                          ) : !isConfirming ? (
                            <motion.button
                              key="trash"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.8, opacity: 0 }}
                              transition={{ duration: 0.15 }}
                              onClick={() => setConfirmDiscardUrl(candidate.profile_url)}
                              title="Discard recommendation"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#d32f2f",
                                cursor: "pointer",
                                padding: "6px",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffebee";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              }}
                            >
                              <FaTrash size={16} />
                            </motion.button>
                          ) : (
                            <motion.div
                              key="confirm"
                              initial={{ scale: 0.9, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.9, opacity: 0 }}
                              transition={{ duration: 0.15 }}
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              <span style={{ fontSize: "11px", fontWeight: "600", color: "#666" }}>
                                Discard?
                              </span>
                              <button
                                onClick={() => handleDiscardConfirm(candidate.profile_url)}
                                title="Yes, discard"
                                style={{
                                  background: "#2e7d32",
                                  border: "none",
                                  color: "white",
                                  cursor: "pointer",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#1b5e20";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#2e7d32";
                                }}
                              >
                                <FaCheck size={9} /> Yes
                              </button>
                              <button
                                onClick={() => setConfirmDiscardUrl(null)}
                                title="No, cancel"
                                style={{
                                  background: "#c62828",
                                  border: "none",
                                  color: "white",
                                  cursor: "pointer",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#b71c1c";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#c62828";
                                }}
                              >
                                <FaTimes size={9} /> No
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Columna Derecha: Ventana al lado con Datos Principales del Perfil */}
      <AnimatePresence>
        {selectedProfile && (
          <motion.div
            key={selectedProfile.profile_url || selectedProfile.public_url || "detail"}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ type: "spring", stiffness: 450, damping: 32 }}
            className="linkedin-detail-pane"
          >
            {/* Header del Detalle */}
            <div className="linkedin-detail-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "4px" }}>
                  <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "700", color: "#0f172a" }}>
                    {selectedName}
                  </h3>
                  <span className={`fit-score-badge ${selectedScoreClass}`} title="AI Fit Alignment Score">
                    Fit Score: {selectedFitScore}/10
                  </span>
                  {selectedProfile.meets_musthaves && (
                    <span className="linkedin-detail-tag success">✓ Mandatory Requirements Met</span>
                  )}
                  {selectedProfile.consulting_pedigree && (
                    <span className="linkedin-detail-tag info">★ Top-Tier Consulting Pedigree</span>
                  )}
                </div>

                {selectedLocation && (
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#64748b", fontSize: "12px" }}>
                    <FaMapMarkerAlt size={12} />
                    <span>{selectedLocation}</span>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {selectedTargetUrl && (
                  <button
                    type="button"
                    className={`linkedin-detail-add-btn ${
                      addingUrls.has(selectedTargetUrl)
                        ? "loading"
                        : addedUrls.has(selectedTargetUrl)
                        ? "added"
                        : ""
                    }`}
                    disabled={addingUrls.has(selectedTargetUrl) || addedUrls.has(selectedTargetUrl)}
                    onClick={() => handleAddProfileViaExtension(selectedProfile)}
                    title={
                      addedUrls.has(selectedTargetUrl)
                        ? "Profile already added to project"
                        : addingUrls.has(selectedTargetUrl)
                        ? "Scraping and adding to project..."
                        : "Add candidate to project via EPH Chrome Extension"
                    }
                  >
                    {addingUrls.has(selectedTargetUrl) ? (
                      <>
                        <FaSpinner className="fa-spin" size={12} />
                        <span>Adding to Project...</span>
                      </>
                    ) : addedUrls.has(selectedTargetUrl) ? (
                      <>
                        <FaCheck size={12} />
                        <span>Added to Project</span>
                      </>
                    ) : (
                      <>
                        <FaUserPlus size={12} />
                        <span>Add to Project</span>
                      </>
                    )}
                  </button>
                )}
                {selectedTargetUrl && (
                  <a
                    href={selectedTargetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="linkedin-detail-external-btn"
                    title="Open profile on LinkedIn in new tab"
                  >
                    <FaLinkedin size={14} />
                    <span>View in LinkedIn</span>
                    <FaExternalLinkAlt size={10} />
                  </a>
                )}
                <button
                  onClick={() => setSelectedProfile(null)}
                  className="linkedin-detail-close-btn"
                  title="Close side window"
                >
                  <FaTimes size={14} />
                </button>
              </div>
            </div>

            {/* Contenido Detallado */}
            <div className="linkedin-detail-body">
              {/* 1. Headline */}
              {selectedProfile.headline && (
                <div className="linkedin-detail-section">
                  <div className="linkedin-detail-section-title">Headline</div>
                  <p style={{ margin: 0, fontSize: "13.5px", color: "#1e293b", lineHeight: "1.5", fontWeight: "500" }}>
                    {selectedProfile.headline}
                  </p>
                </div>
              )}



              {/* 3. Experiencia */}
              <div className="linkedin-detail-section">
                <div className="linkedin-detail-section-title">
                  <FaBriefcase size={13} /> Experience
                </div>
                {parsedExperience.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {parsedExperience.map((exp, idx) => (
                      <div key={idx} className="linkedin-detail-exp-item">
                        {exp}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8", fontStyle: "italic" }}>
                    No experience details recorded for this profile.
                  </p>
                )}
              </div>

              {/* 4. Skills */}
              <div className="linkedin-detail-section">
                <div className="linkedin-detail-section-title">
                  <FaTags size={13} /> Skills & Technologies
                </div>
                {parsedSkills.length > 0 ? (
                  <div className="linkedin-detail-skills-grid">
                    {parsedSkills.map((skill, idx) => (
                      <span key={idx} className="linkedin-detail-skill-chip">
                        {skill}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8", fontStyle: "italic" }}>
                    No skills listed.
                  </p>
                )}
              </div>

              {/* 5. Educación */}
              <div className="linkedin-detail-section">
                <div className="linkedin-detail-section-title">
                  <FaGraduationCap size={13} /> Education
                </div>
                {parsedEducation.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {parsedEducation.map((edu, idx) => (
                      <div key={idx} className="linkedin-detail-edu-item">
                        {edu}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8", fontStyle: "italic" }}>
                    No education details recorded.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LinkedInResultsPanel;
